import React, { useState, useMemo } from 'react';
import { 
  BarChart2, 
  FileText, 
  Download, 
  Filter, 
  Calendar, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  AlertTriangle,
  PieChart,
  MoreHorizontal
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart as RechartsPieChart, 
  Pie, 
  Cell 
} from 'recharts';

import CustomReportGeneration from '@/client/components/admin/Escrow/ReportGeneration/ReportGenerator';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Type Definitions
interface TransactionReport {
  id: string;
  type: 'income' | 'expense';
  category: string;
  amount: number;
  date: string;
}

interface FinancialReport {
  period: string;
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
}

interface UserActivityReport {
  userId: string;
  username: string;
  loginCount: number;
  transactionCount: number;
  lastActive: string;
}

interface RiskReport {
  category: string;
  riskLevel: 'low' | 'medium' | 'high';
  count: number;
  percentage: number;
}

interface PerformanceMetric {
  metric: string;
  value: number;
  trend: 'up' | 'down';
}

const ReportsAndAnalytics: React.FC = () => {
  // Breadcrumb Management
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Analytics and Reports' }
      ]);
  // Date Range State
  const [dateRange, setDateRange] = useState({
    startDate: '2024-01-01',
    endDate: '2024-02-29'
  });

  // Report Data
  const [transactionReports, setTransactionReports] = useState<TransactionReport[]>([
    {
      id: 'TRX001',
      type: 'income',
      category: 'Service Fees',
      amount: 50000,
      date: '2024-02-15'
    },
    {
      id: 'TRX002',
      type: 'expense',
      category: 'Operational Costs',
      amount: 30000,
      date: '2024-02-20'
    }
  ]);

  const [financialReports, setFinancialReports] = useState<FinancialReport[]>([
    {
      period: 'February 2024',
      totalRevenue: 250000,
      totalExpenses: 180000,
      netProfit: 70000
    }
  ]);

  const [userActivityReports, setUserActivityReports] = useState<UserActivityReport[]>([
    {
      userId: 'USER001',
      username: 'john_doe',
      loginCount: 15,
      transactionCount: 7,
      lastActive: '2024-02-25'
    }
  ]);

  const [riskReports, setRiskReports] = useState<RiskReport[]>([
    {
      category: 'High Value Transactions',
      riskLevel: 'medium',
      count: 25,
      percentage: 12.5
    }
  ]);

  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetric[]>([
    {
      metric: 'Total Transactions',
      value: 1250,
      trend: 'up'
    },
    {
      metric: 'Active Users',
      value: 500,
      trend: 'up'
    }
  ]);

  // Render Methods
  const renderTransactionReport = () => {
    // Prepare data for bar chart
    const chartData = transactionReports.map(report => ({
      name: report.category,
      amount: report.amount,
      type: report.type
    }));

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <BarChart2 className="mr-2 h-5 w-5 text-blue-500" /> 
            Transaction Report
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Download className="mr-1 h-4 w-4" /> Export
          </button>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar 
                dataKey="amount" 
                fill="#8884d8" 
                name="Transaction Amount"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {transactionReports.map((report) => (
            <div 
              key={report.id} 
              className={`p-3 rounded-lg ${
                report.type === 'income' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              } border`}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-900">{report.category}</p>
                  <p className={`text-lg font-bold ${
                    report.type === 'income' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {report.type === 'income' ? '+' : '-'}₵{report.amount.toLocaleString()}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  report.type === 'income' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {report.type.charAt(0).toUpperCase() + report.type.slice(1)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFinancialReport = () => {
    const latestReport = financialReports[0];

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="mr-2 h-5 w-5 text-green-500" /> 
            Financial Overview
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Download className="mr-1 h-4 w-4" /> Export
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-xl font-bold text-green-600">
              ₵{latestReport.totalRevenue.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Total Expenses</p>
            <p className="text-xl font-bold text-red-600">
              ₵{latestReport.totalExpenses.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Net Profit</p>
            <p className="text-xl font-bold text-blue-600">
              ₵{latestReport.netProfit.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-sm text-gray-500">
            Financial Period: {latestReport.period}
          </p>
        </div>
      </div>
    );
  };

  const renderUserActivityReport = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="mr-2 h-5 w-5 text-purple-500" /> 
            User Activity
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Download className="mr-1 h-4 w-4" /> Export
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Login Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transactions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userActivityReports.map((report) => (
                <tr key={report.userId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {report.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {report.loginCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {report.transactionCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {report.lastActive}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderRiskReport = () => {
    // Prepare data for pie chart
    const pieData = riskReports.map(report => ({
      name: report.category,
      value: report.count
    }));

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="mr-2 h-5 w-5 text-red-500" /> 
            Risk Analysis
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Download className="mr-1 h-4 w-4" /> Export
          </button>
        </div>
        <div className="h-64 flex">
          <ResponsiveContainer width="60%" height="100%">
            <RechartsPieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPieChart>
          </ResponsiveContainer>
          <div className="w-40 pl-4">
            {riskReports.map((report, index) => (
              <div key={report.category} className="flex items-center mb-2">
                <span 
                  className="inline-block w-3 h-3 mr-2 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <div>
                  <p className="text-sm font-medium">{report.category}</p>
                  <p className={`text-xs ${
                    report.riskLevel === 'high' 
                      ? 'text-red-600' 
                      : report.riskLevel === 'medium' 
                      ? 'text-yellow-600' 
                      : 'text-green-600'
                  }`}>
                    {report.riskLevel.charAt(0).toUpperCase() + report.riskLevel.slice(1)} Risk
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPerformanceMetrics = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <TrendingUp className="mr-2 h-5 w-5 text-blue-500" /> 
            Performance Metrics
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <MoreHorizontal className="mr-1 h-4 w-4" /> More Details
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {performanceMetrics.map((metric) => (
            <div 
              key={metric.metric} 
              className="bg-gray-50 p-4 rounded-lg flex justify-between items-center"
            >
              <div>
                <p className="text-sm text-gray-500">{metric.metric}</p>
                <p className="text-xl font-bold">{metric.value.toLocaleString()}</p>
              </div>
              {metric.trend === 'up' ? (
                <TrendingUp className="h-6 w-6 text-green-500" />
              ) : (
                <TrendingDown className="h-6 w-6 text-red-500" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCustomReportGeneration = () => {
    // State for custom report configuration
    const [reportType, setReportType] = useState('');
    const [selectedFields, setSelectedFields] = useState<string[]>([]);

    // Available report fields
    const availableFields = [
      'Transaction Amount',
      'User ID',
      'Date',
      'Transaction Type',
      'Risk Level',
      'User Activity',
      'Financial Metrics'
    ];

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <PieChart className="mr-2 h-5 w-5 text-purple-500" /> 
            Custom Report Generation
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Download className="mr-1 h-4 w-4" /> Generate Report
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Report Type</option>
              <option value="transactions">Transactions</option>
              <option value="users">User Activity</option>
              <option value="financial">Financial</option>
              <option value="risk">Risk Analysis</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Fields
            </label>
            <div className="grid grid-cols-2 gap-2">
              {availableFields.map((field) => (
                <div key={field} className="flex items-center">
                  <input
                    type="checkbox"
                    id={field}
                    checked={selectedFields.includes(field)}
                    onChange={() => {
                      setSelectedFields(prev => 
                        prev.includes(field)
                          ? prev.filter(f => f !== field)
                          : [...prev, field]
                      );
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label 
                    htmlFor={field} 
                    className="ml-2 block text-sm text-gray-900"
                  >
                    {field}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <BarChart2 className="mr-3 h-8 w-8 text-blue-600" />
            Reports & Analytics
          </h1>

          {/* Date Range Filter */}
          <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Calendar className="mr-2 h-5 w-5 text-gray-500" />
                <span className="text-sm text-gray-700">Date Range:</span>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({
                    ...prev, 
                    startDate: e.target.value
                  }))}
                  className="border rounded px-2 py-1 text-sm"
                />
                <span>to</span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({
                    ...prev, 
                    endDate: e.target.value
                  }))}
                  className="border rounded px-2 py-1 text-sm"
                />
              </div>
            </div>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center">
              <Filter className="mr-2 h-4 w-4" /> Apply Filters
            </button>
          </div>

          {/* Main Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Transaction and Financial Reports */}
            <div>
              {renderTransactionReport()}
            </div>
            <div>
              {renderFinancialReport()}
            </div>
          </div>

          {/* User Activity and Risk Reports */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              {renderUserActivityReport()}
            </div>
            <div>
              {renderRiskReport()}
            </div>
          </div>

          {/* Performance Metrics and Custom Report Generation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              {renderPerformanceMetrics()}
            </div>
            <div>
              <CustomReportGeneration />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsAndAnalytics;